import { useEffect } from 'react';
import { useFloating, offset, autoUpdate, hide } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { getDefaultNode } from 'foundations-components/transitional/utils/RootNode';
import { NEPTUNE_LAYER } from 'foundations-components/transitional/constants';
import { jsx as _jsx } from "react/jsx-runtime";
const getBottomEditorSelectionBounds = editor => {
  const selection = editor.selection.getSel();

  // Handle cases where some browsers include no content in the next node for
  // triple clicks
  const hasNoContentInFocusNode = selection.anchorNode !== selection.focusNode && selection.focusOffset === 0;
  // Only get selection rects if there is an computable selection
  // otherwise return null
  if (!selection || selection.rangeCount === 0) {
    return null;
  }
  const selectionBounds = selection.getRangeAt(0).getClientRects();
  let index = selectionBounds.length - 1;
  if (hasNoContentInFocusNode) {
    const selectionEndNode = selection.focusNode;
    if (selectionEndNode.getBoundingClientRect) {
      const focusNodeBoundingRect = selectionEndNode.getBoundingClientRect();
      const lastRangeClientRect = selectionBounds[selectionBounds.length - 1];
      // If the last bounding rect in our range has the same bottom as the focus node
      // then we know that our last client rect has no selection content
      // Some browsers (like Chrome) include a focus node with no content, but don't include its
      // bounding rect in the client list
      const lastRangeClientRectIncludesNoContent = focusNodeBoundingRect.bottom === lastRangeClientRect.bottom;
      if (lastRangeClientRectIncludesNoContent) {
        index--;
      }
    }
  }
  index = Math.max(index, 0);
  return selectionBounds[index];
};
const DEFAULT_RECT = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  width: 0,
  height: 0,
  x: 0,
  y: 0
};
function HighlightedTextFloaty({
  editor,
  placement = 'bottom',
  children,
  'data-test-id': dataTestId,
  refreshKey
}) {
  var _document$querySelect, _middlewareData$hide;
  const toolbarHeight = (_document$querySelect = document.querySelector('[data-test-id="canvas-toolbar"]')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.getBoundingClientRect().height;
  const {
    refs,
    floatingStyles,
    middlewareData
  } = useFloating({
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [offset(-3), hide({
      padding: toolbarHeight
    })]
  });
  useEffect(() => {
    // We are using the Virtual Elements API to position the floaty
    // so we need to create a bounding rect for the floaty's target
    // See docs here: https://floating-ui.com/docs/virtual-elements
    refs.setPositionReference({
      getBoundingClientRect: () => {
        var _refs$floating$curren;
        const targetBounds = getBottomEditorSelectionBounds(editor);
        if (!targetBounds) {
          return DEFAULT_RECT;
        }
        const contentDimensions = ((_refs$floating$curren = refs.floating.current) === null || _refs$floating$curren === void 0 ? void 0 : _refs$floating$curren.getBoundingClientRect()) || DEFAULT_RECT;
        const rectCoords = {
          // Top position of the floaty
          top: targetBounds.top,
          // Bottom position of the floaty
          bottom: targetBounds.top + targetBounds.height,
          // Left position of the floaty
          left: targetBounds.right - contentDimensions.width / 2,
          // Right position of the floaty
          right: targetBounds.right + contentDimensions.width / 2,
          // Height of the floaty
          height: contentDimensions.height,
          // Width of the floaty
          width: contentDimensions.width,
          // X position of the floaty
          x: targetBounds.right - contentDimensions.width / 2,
          // Y position of the floaty
          y: targetBounds.top
        };
        const editorWindow = editor.getWin();
        if (editorWindow && editorWindow.document !== document) {
          // In this case, the tinymce editor has been rendered inside of a separate iframe
          // so we need to offset our position relative to the container of the iframe
          const container = editor.getContainer();
          if (!container) {
            return rectCoords;
          }
          const containerRect = container.getBoundingClientRect();
          rectCoords.top += containerRect.top;
          rectCoords.bottom += containerRect.bottom;
          rectCoords.left += containerRect.left;
          rectCoords.right += containerRect.right;
        }
        return rectCoords;
      }
    });
  }, [refs, editor]);
  return /*#__PURE__*/createPortal( /*#__PURE__*/_jsx("div", {
    ref: refs.setFloating,
    "data-test-id": dataTestId,
    "data-tinymce-floating-element": "true",
    style: Object.assign({}, floatingStyles, {
      zIndex: NEPTUNE_LAYER,
      visibility: (_middlewareData$hide = middlewareData.hide) !== null && _middlewareData$hide !== void 0 && _middlewareData$hide.referenceHidden ? 'hidden' : 'visible'
    }),
    children: children
  }, refreshKey), getDefaultNode());
}
export default HighlightedTextFloaty;